<script setup lang="ts">
import type { Component } from 'vue'
import { useI18n } from 'vue-i18n'
import { MagicWandIcon, PenIcon, UndoLeftRoundIcon } from '@solar-icons/vue/linear'
import AppBadge from '../AppBadge/AppBadge.vue'
import AppButton from '../AppButton/AppButton.vue'
import AppScrollArea from '../AppScrollArea/AppScrollArea.vue'
import AppSkeleton from '../AppSkeleton/AppSkeleton.vue'
import RaldoMark from '../RaldoMark/RaldoMark.vue'
import { useFormatters } from '../../../composables/useFormatters'
import type {
  RevisionEntry,
  RevisionEntryAuthor,
  RevisionEntryReason,
} from './revision-history-types'

const { t } = useI18n()

const props = withDefaults(
  defineProps<{
    /** The chain, oldest first. */
    revisions: RevisionEntry[]
    /** Id of the revision the object currently points at. */
    currentRevisionId?: string
    loading?: boolean
  }>(),
  {
    currentRevisionId: undefined,
    loading: false,
  },
)

const emit = defineEmits<{
  restore: [revisionId: string]
}>()

const REVISION_ICON: Record<Exclude<RevisionEntryReason, 'generated'>, Component> = {
  edited: PenIcon,
  rewritten: MagicWandIcon,
  restored: UndoLeftRoundIcon,
}

const AUTHOR_LABEL: Record<RevisionEntryAuthor, string> = {
  raldo: 'Raldo',
  user: '',
}

const REVISION_SUMMARY_KEY: Record<RevisionEntryReason, string> = {
  generated: 'revisionHistory.generated',
  edited: 'revisionHistory.edited',
  rewritten: 'revisionHistory.rewritten',
  restored: 'revisionHistory.restored',
}

function summary(revision: RevisionEntry): string {
  const author = revision.author === 'user' ? t('common.you') : AUTHOR_LABEL[revision.author]
  return t(REVISION_SUMMARY_KEY[revision.reason], { author })
}

const { formatDateTime } = useFormatters()

function when(date: Date): string {
  return formatDateTime(date, 'weekdayMonthDay')
}

function isCurrent(revision: RevisionEntry): boolean {
  return revision.id === props.currentRevisionId
}
</script>

<template>
  <div v-if="loading" class="flex flex-col gap-1">
    <p class="sr-only">{{ t('revisionHistory.loading') }}</p>
    <div v-for="n in 3" :key="n" class="flex h-16 items-center" aria-hidden="true">
      <div class="h-6 w-full"><AppSkeleton :rounded="2" /></div>
    </div>
  </div>

  <AppScrollArea
    v-else-if="revisions.length"
    role="list"
    class="flex max-h-(--history-max-height) flex-col"
  >
    <div
      v-for="(revision, index) in revisions"
      :key="revision.id"
      role="listitem"
      class="flex gap-4"
    >
      <div class="flex flex-col items-center gap-1">
        <RaldoMark v-if="revision.reason === 'generated'" size="xs" />
        <component
          :is="REVISION_ICON[revision.reason]"
          v-else
          class="shrink-0 text-icon-secondary"
        />
        <span
          v-if="index < revisions.length - 1"
          aria-hidden="true"
          class="w-0 flex-1 border-l border-border"
        />
      </div>
      <div
        class="flex min-w-0 flex-1 items-start justify-between gap-4"
        :class="{ 'pb-8': index < revisions.length - 1 }"
      >
        <div class="flex min-w-0 flex-col gap-1">
          <p class="text-body-sm text-text-secondary">{{ summary(revision) }}</p>
          <p v-if="revision.instruction" class="text-body-sm text-text-tertiary">
            {{ t('revisionHistory.instructionAsked', { instruction: revision.instruction }) }}
          </p>
          <p class="text-caption text-text-tertiary">{{ when(revision.createdAt) }}</p>
        </div>
        <AppBadge v-if="isCurrent(revision)" tone="blue">{{
          t('revisionHistory.current')
        }}</AppBadge>
        <AppButton
          size="sm"
          v-else
          icon-only
          :start-icon="UndoLeftRoundIcon"
          variant="ghost"
          :title="t('revisionHistory.restore')"
          :aria-label="t('revisionHistory.restoreAria', { date: when(revision.createdAt) })"
          @click="emit('restore', revision.id)"
        />
      </div>
    </div>
  </AppScrollArea>
</template>
